/**
 * QChatWindow Component (v3.0 - Bolt/Claude Inspired)
 * Professional chat interface with pure dark theme and crisp typography
 *
 * Key Improvements v3.0:
 * - Pure dark background (#0A0A0F) like Bolt.new
 * - Crisp white text (#F5F5F5)
 * - Minimal header design
 * - Clean input area
 *
 * Layout:
 * ┌─────────────────────────────────────┐
 * │  Header (Q + Tagline + New)         │
 * ├─────────────────────────────────────┤
 * │  Messages Area (scrollable, flex)   │
 * ├─────────────────────────────────────┤
 * │  Input Area (fixed)                 │
 * └─────────────────────────────────────┘
 */

import React, { useRef, useCallback, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Collapse } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QChatMessages from './QChatMessages';
import QChatInput from './QChatInput';

/**
 * QChatWindow Component
 * @param {object} props
 * @param {array} props.messages - Chat messages array
 * @param {number} props.balance - Token balance
 * @param {boolean} props.isLoading - AI is responding
 * @param {boolean} props.isLowBalance - Balance <= 15
 * @param {boolean} props.canSend - Can send messages
 * @param {function} props.onSendMessage - Send message handler
 * @param {function} props.onActionCard - Action card click handler
 * @param {function} props.onActionChip - Action chip click handler
 * @param {function} props.onCancelStream - Cancel streaming handler
 * @param {function} props.onBuyTokens - Buy tokens handler
 * @param {function} props.onClearChat - Clear chat handler
 * @param {string} props.error - Error message if any
 */
const QChatWindow = ({
  messages = [],
  balance = 0,
  isLoading = false,
  isLowBalance = false,
  canSend = true,
  onSendMessage,
  onActionCard,
  onActionChip,
  onCancelStream,
  onBuyTokens,
  onClearChat,
  error = null
}) => {
  const inputRef = useRef(null);
  const [isInputExpanded, setIsInputExpanded] = useState(false);

  // Handle action card - focus input if needed
  const handleActionCard = useCallback((actionId) => {
    const result = onActionCard?.(actionId);
    if (result?.focusInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [onActionCard]);

  // Handle send
  const handleSend = useCallback((message) => {
    onSendMessage?.(message, 'chat');
    setIsInputExpanded(false);
  }, [onSendMessage]);

  // Toggle input expansion
  const toggleInputExpand = useCallback(() => {
    setIsInputExpanded(prev => !prev);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: '#0A0A0F',
        borderRadius: '0', // No border radius - clean Bolt style
        overflow: 'hidden',
        border: 'none' // No border - cleaner
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER - Compact Bolt-style
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          backgroundColor: '#0A0A0F'
        }}
      >
        {/* Left: Q Logo + Tagline - Institutional */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Q Icon - Prominent */}
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(39, 181, 109, 0.25)'
            }}
          >
            <Typography
              sx={{
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: 1
              }}
            >
              Q
            </Typography>
          </Box>
          {/* Title + Tagline */}
          <Box>
            <Typography
              sx={{
                color: '#F5F5F5',
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: 1.2
              }}
            >
              Q AI Coach
            </Typography>
            <Typography
              sx={{
                color: '#52525B',
                fontSize: '0.625rem',
                fontWeight: 500,
                letterSpacing: '0.02em'
              }}
            >
              Your Trading Intelligence
            </Typography>
          </Box>
        </Box>

        {/* Right: New Chat Button - Minimal */}
        {onClearChat && messages.length > 1 && (
          <Tooltip title="New conversation" arrow placement="left">
            <IconButton
              onClick={onClearChat}
              disabled={isLoading}
              size="small"
              sx={{
                color: '#52525B',
                gap: 0.5,
                borderRadius: '4px',
                padding: '4px 8px',
                backgroundColor: 'transparent',
                '&:hover': {
                  color: '#27B56D',
                  backgroundColor: 'rgba(39, 181, 109, 0.06)'
                },
                '&.Mui-disabled': {
                  color: '#27272A'
                }
              }}
            >
              <AddIcon sx={{ fontSize: '0.875rem' }} />
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500 }}>
                New
              </Typography>
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          MESSAGES AREA - Takes all available space
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <QChatMessages
          messages={messages}
          onActionCard={handleActionCard}
          onActionChip={onActionChip}
          isLoading={isLoading}
        />
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          ERROR DISPLAY - Minimal Bolt-style
          ════════════════════════════════════════════════════════════════════════ */}
      <Collapse in={!!error}>
        <Box
          sx={{
            padding: '6px 16px',
            backgroundColor: 'rgba(239, 68, 68, 0.06)',
            borderTop: '1px solid rgba(239, 68, 68, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              backgroundColor: '#EF4444'
            }}
          />
          <Typography
            sx={{
              color: '#EF4444',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          >
            {error}
          </Typography>
        </Box>
      </Collapse>

      {/* ════════════════════════════════════════════════════════════════════════
          INPUT AREA - Compact Bolt-style
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.04)',
          backgroundColor: '#0A0A0F'
        }}
      >
        {/* Input Component - Compact */}
        <Box sx={{ px: 1, py: 0.75 }}>
          <QChatInput
            ref={inputRef}
            onSend={handleSend}
            onCancel={onCancelStream}
            isLoading={isLoading}
            disabled={!canSend}
            expanded={isInputExpanded}
            placeholder={
              balance === 0
                ? 'Purchase tokens to continue...'
                : isLowBalance
                ? 'Low balance — add tokens'
                : 'Message Q...'
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default QChatWindow;
